import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronLeft, X } from 'lucide-react'
import { APP_NAME, APP_SUBTITLE } from '@/config/env'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { usePermissions } from '@/lib/auth'
import { cn } from '@/lib/utils/cn'
import logoUrl from '@/assets/woctools-logo.png'
import { filterNavigation, NAVIGATION, type NavGroup, type NavNode } from '../navigation'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
  mobileOpen: boolean
  onCloseMobile: () => void
}

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-[0.625rem] px-3 py-2 text-sm font-medium transition-colors',
    // Item ativo preenchido com o roxo da marca (Opção C); ícone em lilás.
    isActive
      ? 'bg-brand-700 text-white shadow-[0_6px_18px_-8px_rgb(123_44_191_/_0.8)] [&>svg]:text-brand-200'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  )

function containsPath(node: NavNode, pathname: string): boolean {
  if (node.kind === 'leaf') return pathname === node.to || pathname.startsWith(`${node.to}/`)
  return node.children.some((child) => containsPath(child, pathname))
}

function NavNodes({
  nodes,
  collapsed,
  depth,
  onNavigate,
}: {
  nodes: NavNode[]
  collapsed: boolean
  depth: number
  onNavigate: () => void
}) {
  return (
    <>
      {nodes.map((node) =>
        node.kind === 'leaf' ? (
          <NavLink
            key={node.id}
            to={node.to}
            className={(state) => cn(navItemClass(state), !collapsed && depth > 1 && 'pl-9')}
            onClick={onNavigate}
            title={collapsed ? node.label : undefined}
          >
            <node.icon className="h-4 w-4 shrink-0" aria-hidden />
            {collapsed ? <span className="sr-only">{node.label}</span> : <span>{node.label}</span>}
          </NavLink>
        ) : (
          <NavSubgroup
            key={node.id}
            group={node}
            collapsed={collapsed}
            depth={depth}
            onNavigate={onNavigate}
          />
        ),
      )}
    </>
  )
}

/** Subgrupo recolhível (ex.: Operacional › Agendamentos). */
function NavSubgroup({
  group,
  collapsed,
  depth,
  onNavigate,
}: {
  group: NavGroup
  collapsed: boolean
  depth: number
  onNavigate: () => void
}) {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(true)
  const active = containsPath(group, pathname)
  const Icon = group.icon
  const listId = `nav-${group.id}`

  if (collapsed) {
    return <NavNodes nodes={group.children} collapsed depth={depth + 1} onNavigate={onNavigate} />
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex w-full items-center gap-3 rounded-[0.625rem] px-3 py-2 text-sm font-medium transition-colors hover:bg-muted',
          active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
      </button>
      {open ? (
        <div id={listId} className="mt-0.5 space-y-0.5">
          <NavNodes
            nodes={group.children}
            collapsed={false}
            depth={depth + 1}
            onNavigate={onNavigate}
          />
        </div>
      ) : null}
    </div>
  )
}

export function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile }: SidebarProps) {
  const permissions = usePermissions()
  const sections = filterNavigation(NAVIGATION, permissions.can)

  const renderContent = (isCollapsed: boolean) => (
    <div className="flex h-full flex-col">
      <div className="flex h-[4.5rem] items-center gap-3 px-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-700 shadow-[0_6px_16px_-6px_rgb(90_24_154_/_0.7)]">
          <img src={logoUrl} alt={`Logo ${APP_NAME}`} className="h-[1.625rem] w-[1.625rem]" />
        </span>
        {!isCollapsed ? (
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-bold tracking-tight">{APP_NAME}</p>
            <p className="truncate text-xs text-primary-text">{APP_SUBTITLE}</p>
          </div>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className="hidden lg:inline-flex"
          onClick={onToggleCollapsed}
          aria-label={isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
        >
          <ChevronLeft
            className={cn('h-4 w-4 transition-transform', isCollapsed && 'rotate-180')}
          />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onCloseMobile}
          aria-label="Fechar menu"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <nav
        aria-label="Navegação principal"
        className="flex-1 space-y-1 overflow-y-auto px-3 pb-3 pt-2 scrollbar-thin"
      >
        {permissions.isLoading ? (
          <div className="space-y-2 p-1" aria-hidden>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          sections.map((section, index) => (
            <div key={section.id} className="space-y-0.5">
              {!isCollapsed ? (
                <p
                  className={cn(
                    'px-3 pb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                    index === 0 ? 'pt-1' : 'pt-5',
                  )}
                >
                  {section.label}
                </p>
              ) : index > 0 ? (
                <div className="my-3 h-px bg-border/60" aria-hidden />
              ) : null}
              <NavNodes
                nodes={section.children}
                collapsed={isCollapsed}
                depth={1}
                onNavigate={onCloseMobile}
              />
            </div>
          ))
        )}
        {!permissions.isLoading && sections.length === 0 && !isCollapsed ? (
          <p className="px-2.5 py-3 text-xs text-muted-foreground">
            Seu perfil ainda não tem acesso a nenhuma área.
          </p>
        ) : null}
      </nav>
    </div>
  )

  return (
    <>
      <aside
        className={cn(
          'hidden shrink-0 bg-card transition-[width] duration-200 lg:block',
          collapsed ? 'w-[4.5rem]' : 'w-64',
        )}
      >
        {renderContent(collapsed)}
      </aside>

      {mobileOpen ? (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div
            className="absolute inset-0 animate-fade-in bg-slate-950/60"
            onClick={onCloseMobile}
            aria-hidden
          />
          <div className="absolute inset-y-0 left-0 w-72 animate-slide-up bg-card shadow-2xl">
            {renderContent(false)}
          </div>
        </div>
      ) : null}
    </>
  )
}
